import { StanInput, StanModal } from '@/components';
import { Editor } from '@tiptap/react';
import { useEffect, useMemo, useState } from 'react';
import IcSearch from '@/assets/icons/ic-search.svg';
import { PageStatus } from '@/types/store';
import { Product } from '@/types/pageSchema';
import { useStoreStore } from '@/stores/storeStore';
import { useUser } from '@/queries/useUser';
import { buildProducts } from '@/lib/helpers';
import { useDefaultStore } from '@/stores';
import { createIconElement } from '@/lib';

export const ProductSelectionModal = ({
    isOpen,
    onClose,
    editor,
}: {
    isOpen: boolean;
    onClose: () => void;
    editor: Editor;
}) => {
    const [searchInput, setSearchInput] = useState('');
    const store = useDefaultStore(state => state.store);
    const isLoading = useStoreStore(state => state.isLoading);
    const { user } = useUser();

    const handleClose = () => {
        setSearchInput('');
        onClose();
    };

    const products = useMemo(() => {
        if (!store || !user) return [];
        return buildProducts({
            store,
            funnels: [],
            filters: {
                status: PageStatus.DRAFT,
            },
            format: 'products',
            user,
        }) as Product[];
    }, [store, user]);

    // Filter products based on search input
    const filteredProducts = useMemo(() => {
        if (!searchInput.trim()) return products;
        return products.filter(product => product.title.toLowerCase().includes(searchInput.toLowerCase()));
    }, [products, searchInput]);

    const confirmSelection = (product: Product) => {
        // Insert product link into the editor
        const linkHTML = `<a href="${product.url}" target="_blank">${product.title}</a>`;

        editor.chain().focus().insertContent(linkHTML).run();
        handleClose();
    };

    // Auto-focus search input when modal opens
    useEffect(() => {
        if (isOpen) {
            // Small delay to ensure modal is fully rendered
            setTimeout(() => {
                const searchInput = document.getElementById('product-search-input');
                searchInput?.focus();
            }, 100);
        }
    }, [isOpen]);

    return (
        <StanModal
            id='product-selection-modal'
            isOpen={isOpen}
            onClose={handleClose}
            title='Add Product Link'
            size='md'
        >
            <div className='mb-4 flex flex-col gap-4'>
                <StanInput
                    id='product-search-input'
                    innerLabel='Search Products'
                    name='product-search'
                    type='text'
                    value={searchInput}
                    onChange={e => setSearchInput(e.target.value)}
                    startIcon={createIconElement(IcSearch)}
                    placeholder='Search...'
                />

                <div className='products-container'>
                    <div
                        className='border-neutral-dimmed no-scrollbar overflow-auto rounded-sm border'
                        style={{ height: '200px' }}
                    >
                        {isLoading ? (
                            <div className='text-gray flex h-full items-center justify-center'>Loading products...</div>
                        ) : filteredProducts.length === 0 ? (
                            <div className='text-gray flex h-full items-center justify-center'>
                                {searchInput ? 'No products found' : 'No products available'}
                            </div>
                        ) : (
                            filteredProducts.map(product => (
                                <div
                                    key={product.id}
                                    onClick={() => confirmSelection(product)}
                                    className='border-neutral-dimmed hover:bg-neutral-subtle flex cursor-pointer items-center border-b p-2 transition last:border-b-0'
                                >
                                    <img
                                        src={product.image}
                                        alt={product.title}
                                        className='border-neutral-main h-9 w-9 flex-shrink-0 rounded border object-cover'
                                        onError={e => {
                                            // Fallback for broken images
                                            (e.target as HTMLImageElement).src = '/images/placeholder-product.png';
                                        }}
                                    />
                                    <div className='ml-3 min-w-0 flex-1'>
                                        <div className='text-dark truncate text-sm font-medium'>{product.title}</div>
                                        {/* TODO: Price doesn't exist on Product type */}
                                        {/* {product.price?.amount && (
                                            <div className='text-gray text-xs'>
                                                ${(product.price.amount / 100).toFixed(2)}{' '}
                                                {product.price.currency?.toUpperCase()}
                                            </div>
                                        )} */}
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            </div>
        </StanModal>
    );
};
